import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import Spinner from '../../components/ui/Spinner';
import MessageBox from '../../components/ui/MessageBox';
import Button from '../../components/ui/Button';
import Heading from "../../components/ui/Heading";
import SearchInput from "../../components/ui/SearchInput";
import { PlusCircle, FileEdit } from "lucide-react";

// ---------------- Customer Card ----------------
const CustomerCard = ({ customer, onEdit }) => (
  <div className="flex flex-col justify-between p-3 bg-white border rounded-lg shadow-sm">
    <div>
      <h3 className="text-base font-semibold text-gray-800">{customer.Vname}</h3>
      <p className="text-sm text-gray-600">{customer.Mobile}</p>
      <p className="text-xs text-gray-500">{customer.City}</p>
      <p className="text-xs font-medium text-gray-500">
        Balance: <span className="text-gray-800">{customer.Balance}</span>
      </p>
    </div>
    <Button
      className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
             transition-all duration-300 ease-in-out"
      onClick={onEdit}
    >
      <FileEdit className="w-5 h-5" />
      <span className="font-medium">Edit</span>
    </Button>
  </div>
);

const CustomerTableRow = ({ customer, onEdit }) => (
  <tr className="border-b hover:bg-gray-50">
    <td className="px-4 py-2 text-sm">{customer.Vname}</td>
    <td className="px-4 py-2 text-sm">{customer.Mobile}</td>
    <td className="px-4 py-2 text-sm">{customer.City}</td>
    <td className="px-4 py-2 text-sm font-medium">{customer.Balance}</td>
    <td className="px-4 py-2 text-sm">
      <div className="flex justify-center items-center h-full">
        <Button
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white 
                     bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
                     hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
                     transition-all duration-300 ease-in-out"
          onClick={onEdit}
        >
          <FileEdit className="w-4 h-4" />
          Edit
        </Button>
      </div>
    </td>
  </tr>
);


const CustomerListPage = ({ setView, setEditingContext }) => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('card'); // 'card' | 'table'

  useEffect(() => {
    api.getCustomerListWithBalance(user.UsrName)
      .then(data => {
        const list = data.Table || [];
        setCustomers(list);
        setFilteredCustomers(list);
      })
      .catch(() => setError('Failed to fetch customers.'))
      .finally(() => setLoading(false));
  }, [user.UsrName]);

  // ---------------- Filter customers ----------------
  useEffect(() => {
    const filtered = customers.filter(c => {
      const nameMatch = c.Vname?.toLowerCase().includes(searchTerm.toLowerCase());
      const mobileMatch = c.Mobile?.toLowerCase().includes(searchTerm.toLowerCase());
      const addressMatch =
        c.Address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.City?.toLowerCase().includes(searchTerm.toLowerCase());
      return nameMatch || mobileMatch || addressMatch;
    });
    setFilteredCustomers(filtered);
  }, [searchTerm, customers]);

  return (
    <div className="flex flex-col h-full">
      
      <div className="px-3 py-3 bg-grey border-b">
        
        <div className="mb-3">
          <Heading>Customer Management</Heading>
        </div>

        
        <div className="flex items-center gap-2 mb-3">
   
          <Button
            className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
              rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
            onClick={() => setView("add-customer")}
          >
            <PlusCircle size={18} />
            <span className="font-medium">New</span>
          </Button>

          <div className="flex-1" />
          
          <div className="flex items-center justify-center p-1 bg-white-100 rounded-lg">
      
            <button
              onClick={() => setViewMode("card")}
              className={`p-2 rounded ${viewMode === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z" />
              </svg>
            </button>

            
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded ${viewMode === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Row 3: Back + Search Box */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
            onClick={() => setView("list")}
          >
            ⬅
          </Button>

          <SearchInput
            placeholder="Search customers..."
            className="flex-1"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 flex-1 overflow-auto">
        {loading ? (
          <Spinner />
        ) : error ? (
          <MessageBox message={error} />
        ) : filteredCustomers.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            <p className="mt-2 text-sm">No customers found.</p>
          </div>
        ) : viewMode === 'card' ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {filteredCustomers.map(customer => (
              <CustomerCard
                key={customer.Vid}
                customer={customer}
                onEdit={() => {
                  setEditingContext(customer.Vid);
                  setView('edit-customer');
                }}
              />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border rounded-lg">
              <thead className="bg-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Mobile</th>
                  <th className="px-4 py-2 text-left">City</th>
                  <th className="px-4 py-2 text-left">Balance</th>
                  <th className="px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map(customer => (
                  <CustomerTableRow
                    key={customer.Vid}
                    customer={customer}
                    onEdit={() => {
                      setEditingContext(customer.Vid);
                      setView('edit-customer');
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerListPage;


